import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube } from "lucide-react";
import { CONTACT_EMAIL, SITE_URL } from "@/lib/contactEmails";
import { LEGAL_NAV_LINKS, SOCIAL_LINKS } from "@/lib/siteLinks";
import { ContactMailLink } from "./ContactMailLink";

const COPYRIGHT_YEAR = new Date().getFullYear();

function XIcon({ className }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

const SOCIAL_ICONS = {
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube,
    x: XIcon,
};

function SocialLinks({ className = "", iconClassName = "h-3.5 w-3.5", buttonClassName }) {
    const btn =
        buttonClassName ||
        "inline-grid size-7 place-items-center rounded-full text-white/45 transition-colors hover:bg-white/[0.04] hover:text-white";
    return (
        <ul className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="site-footer-social">
            {SOCIAL_LINKS.map(({ id, label, href }) => {
                const Icon = SOCIAL_ICONS[id];
                return (
                    <li key={id}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={label}
                            aria-label={label}
                            className={btn}
                            data-testid={`social-link-${id}`}
                        >
                            <Icon className={iconClassName} />
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

function LegalNavLinks({ className = "", linkClassName = "text-white/45 hover:text-white hover:underline" }) {
    return (
        <nav className={className} aria-label="Legal">
            {LEGAL_NAV_LINKS.map(({ to, label }, i) => (
                <span key={to}>
                    {i > 0 ? <span className="text-white/20"> · </span> : null}
                    <Link to={to} className={linkClassName} data-testid={`footer-link-${to.slice(1)}`}>
                        {label}
                    </Link>
                </span>
            ))}
            <span className="text-white/20"> · </span>
            <ContactMailLink email={CONTACT_EMAIL} className={`${linkClassName} hover:text-[#00BF0D]`} />
        </nav>
    );
}

/** Document footer — scrolls with page content (not viewport-fixed). */
export function SiteFooter({ variant = "compact", className = "" }) {
    if (variant === "full") {
        return (
            <footer
                className={`site-footer site-footer--full border-t border-white/[0.06] pt-6 pb-2 ${className}`}
                data-testid="site-footer"
            >
                <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6 sm:gap-y-3">
                    <p className="text-center text-[11px] text-white/35 sm:text-left">
                        © {COPYRIGHT_YEAR}{" "}
                        <span className="text-white/50">
                            Fair<span className="text-[#00BF0D]">Share</span>
                        </span>
                        {" · "}
                        <a
                            href={SITE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#00BF0D] hover:underline"
                            data-testid="footer-site-url"
                        >
                            {SITE_URL.replace(/^https?:\/\//, "")}
                        </a>
                    </p>

                    <div className="flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-x-4">
                        <LegalNavLinks
                            className="text-center text-[11px] leading-relaxed sm:text-right"
                            linkClassName="text-white/45 hover:text-white hover:underline"
                        />
                        <Link
                            to="/faq"
                            className="text-center text-[11px] text-white/40 hover:text-white hover:underline sm:text-right"
                            data-testid="footer-link-faq"
                        >
                            Help & FAQ
                        </Link>
                        <SocialLinks className="justify-center sm:justify-end" />
                    </div>
                </div>
            </footer>
        );
    }

    return (
        <footer className={`site-footer site-footer--compact text-center ${className}`} data-testid="site-footer">
            <LegalNavLinks className="text-[11px] leading-relaxed text-white/35" linkClassName="text-white/45 hover:text-white hover:underline" />
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                <SocialLinks />
                <span className="text-[10px] text-white/25">© {COPYRIGHT_YEAR} FairShare</span>
            </div>
        </footer>
    );
}

/** @deprecated Prefer SiteFooter — kept for existing imports. */
export function LegalFooter(props) {
    return <SiteFooter variant="compact" {...props} />;
}
